import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FeedIcon from '@mui/icons-material/Feed';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '120px 60px',
  background: '#0a0a0f',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.3), transparent)'
  },
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto'
});

const SectionHeader = styled(Box)({
  textAlign: 'center',
  marginBottom: '64px',
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(34, 197, 94, 0.1)',
  border: '1px solid rgba(34, 197, 94, 0.2)',
  borderRadius: '50px',
  marginBottom: '24px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#4ade80',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 700,
  color: '#ffffff',
  lineHeight: 1.2,
  letterSpacing: '-1px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #22c55e 0%, #4ade80 50%, #86efac 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const PostsGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '24px',
  '@media (max-width: 1024px)': {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr'
  }
});

const PostCard = styled(Box)(({ index }) => ({
  background: 'rgba(255, 255, 255, 0.03)',
  borderRadius: '24px',
  overflow: 'hidden',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeInUp} 0.8s ease-out`,
  animationDelay: `${index * 0.1}s`,
  animationFillMode: 'backwards',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: 'rgba(34, 197, 94, 0.3)',
    boxShadow: '0 20px 50px rgba(34, 197, 94, 0.1)',
    '& .post-image img': {
      transform: 'scale(1.08)'
    },
    '& .arrow-icon': {
      transform: 'translate(4px, -4px)',
      opacity: 1
    }
  }
}));

const PostImage = styled(Box)({
  width: '100%',
  height: '200px',
  overflow: 'hidden',
  position: 'relative',
  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(74, 222, 128, 0.05) 100%)',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
  }
});

const PostImagePlaceholder = styled(Box)({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(74, 222, 128, 0.05) 100%)',
  '& svg': {
    fontSize: '48px',
    color: 'rgba(74, 222, 128, 0.4)'
  }
});

const PostContent = styled(Box)({
  padding: '24px'
});

const PostMeta = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px'
});

const PostDate = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  fontWeight: 500,
  color: '#4ade80',
  '& svg': {
    fontSize: '16px'
  }
});

const PostType = styled(Box)({
  padding: '6px 14px',
  background: 'rgba(34, 197, 94, 0.1)',
  borderRadius: '50px',
  fontFamily: "'Inter', sans-serif",
  fontSize: '11px',
  fontWeight: 600,
  color: '#4ade80',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
});

const PostTitle = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontSize: '20px',
  fontWeight: 600,
  color: '#ffffff',
  marginBottom: '12px',
  lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const PostExcerpt = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.7,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
});

const PostFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: '20px',
  paddingTop: '20px',
  borderTop: '1px solid rgba(255, 255, 255, 0.06)'
});

const ReadMore = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.6)'
});

const ArrowButton = styled(Box)({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '18px',
    color: '#ffffff',
    opacity: 0.5,
    transition: 'all 0.3s ease'
  }
});

const GMBFeedSection = ({ gmbFeed }) => {
  const posts = gmbFeed?.posts || (Array.isArray(gmbFeed) ? gmbFeed : []);

  if (!posts || posts.length === 0) return null;

  const displayPosts = posts.slice(0, 6);

  const formatDate = (dateString) => {
    if (!dateString) return 'Recent';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <SectionContainer id="updates">
      <ContentWrapper>
        <SectionHeader>
          <SectionBadge>
            <FeedIcon sx={{ fontSize: 16, color: '#4ade80' }} />
            <BadgeText>Latest Updates</BadgeText>
          </SectionBadge>
          <SectionTitle>
            Recent <GradientText>Posts</GradientText>
          </SectionTitle>
        </SectionHeader>

        <PostsGrid>
          {displayPosts.map((post, index) => (
            <PostCard key={post.id || index} index={index}>
              <PostImage className="post-image">
                {(post.image || post.mediaUrl || post.media?.[0]?.googleUrl || post.media?.[0]?.sourceUrl) ? (
                  <img src={post.image || post.mediaUrl || post.media?.[0]?.googleUrl || post.media?.[0]?.sourceUrl} alt={post.title || 'Post'} />
                ) : (
                  <PostImagePlaceholder>
                    <FeedIcon />
                  </PostImagePlaceholder>
                )}
              </PostImage>
              <PostContent>
                <PostMeta>
                  <PostDate>
                    <CalendarTodayIcon />
                    {formatDate(post.date || post.createTime)}
                  </PostDate>
                  <PostType>{post.type || post.topicType || 'Update'}</PostType>
                </PostMeta>
                <PostTitle>{post.title || post.summary?.split(' ').slice(0, 6).join(' ') + '...' || 'Business Update'}</PostTitle>
                <PostExcerpt>{post.content || post.summary || 'Check out our latest update!'}</PostExcerpt>
                <PostFooter>
                  <ReadMore>Read more</ReadMore>
                  <ArrowButton>
                    <ArrowOutwardIcon className="arrow-icon" />
                  </ArrowButton>
                </PostFooter>
              </PostContent>
            </PostCard>
          ))}
        </PostsGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default GMBFeedSection;
